import { useId, type MouseEvent as ReactMouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, UncontrolledTooltip } from 'reactstrap';
import { calculatePickingIndexes, HEAT_EMOJI } from '../../api/pickingIndexes';
import type { AssignmentPickListSummary, MissionStatus } from '../../api/assignments';

export type AssignmentCardKind = 'whole_list' | 'self_claim_shard' | 'wizard_shard';

/**
 * Tinte-sfondo per distinta splittata: tutte le shard della STESSA pick-list
 * condividono lo stesso colore, così si riconoscono a colpo d'occhio anche
 * quando sono distribuite tra operatori diversi o ancora nel pool.
 * Hue a bassa opacità (`33` ≈ 0.20) → resta leggibile sia in light che in dark.
 */
const SPLIT_HUES = [
  '#4b9cf5', '#0ab39c', '#e6a91e', '#f1734f', '#ec4e8e', '#8b5cf6', '#4f5bd5', '#b5744a',
  '#29b6c9', '#d4a017', '#e0567a', '#7c8cf8', '#c14fd0', '#5a8f9e', '#f28c28', '#9b8cff',
] as const;
export const SPLIT_TINTS = SPLIT_HUES.map((h) => `${h}33`);

/** Tinta-sfondo stabile per distinta (deterministica su `pickListId`). */
export function splitTintOf(pickListId: number): string {
  const idx = Math.abs(Math.trunc(pickListId)) % SPLIT_TINTS.length;
  return SPLIT_TINTS[idx];
}

export interface AssignmentCardProps {
  kind: AssignmentCardKind;
  location: 'pool' | 'op';
  pickList: AssignmentPickListSummary;
  shardIndex?: number;
  shardTotal?: number;
  claimOrder?: number | null;
  qtyAssignedTotal?: number;
  taskCount?: number;
  /** Mission lifecycle status; only meaningful when location === 'op'. */
  status?: MissionStatus;
  isSelected?: boolean;
  onClick: (e: ReactMouseEvent) => void;
  onSplit?: (pickListId: number) => void;
}

interface StateBadge {
  bg: string;       // Velzon `bg-*-subtle` class
  text: string;     // Velzon `text-*` class
  icon: string;     // Bootstrap Icons class(es)
  labelKey: string; // i18n key in `assignment` namespace
}

/**
 * Palette Velzon per gli stati delle card della board.
 *  · Pool whole-list = secondary (lista in attesa di assegnazione)
 *  · Pool shard      = warning  ("Da assegnare")
 *  · Op pending      = warning  ("In coda")
 *  · Op assigned     = info     ("Assegnata")
 *  · Op active       = primary  (verde brand — "In corso")
 *  · Op done         = success  ("Completata") + leggera opacity
 */
function badgeFor(kind: AssignmentCardKind, location: 'pool' | 'op', status?: MissionStatus): StateBadge {
  if (kind === 'whole_list' && location === 'pool') {
    return { bg: 'bg-secondary-subtle', text: 'text-secondary', icon: 'bi bi-stack', labelKey: 'card.wholeList' };
  }
  if (location === 'pool') {
    return { bg: 'bg-warning-subtle', text: 'text-warning', icon: 'bi bi-clock-history', labelKey: 'card.toAssign' };
  }
  switch (status) {
    case 'active':
      return { bg: 'bg-primary-subtle', text: 'text-primary', icon: 'bi bi-soundwave', labelKey: 'card.inProgress' };
    case 'awaiting_close':
      return { bg: 'bg-warning-subtle', text: 'text-warning', icon: 'bi bi-hourglass-split', labelKey: 'card.awaitingClose' };
    case 'done':
      return { bg: 'bg-success-subtle', text: 'text-success', icon: 'bi bi-check2-all', labelKey: 'card.completed' };
    case 'assigned':
      return { bg: 'bg-info-subtle', text: 'text-info', icon: 'bi bi-person-plus', labelKey: 'card.assigned' };
    case 'pending':
    default:
      return { bg: 'bg-warning-subtle', text: 'text-warning', icon: 'bi bi-clock-history', labelKey: 'card.queued' };
  }
}

export function AssignmentCard({
  kind, location, pickList, shardIndex, shardTotal, claimOrder,
  qtyAssignedTotal, taskCount, status, isSelected, onClick,
}: AssignmentCardProps) {
  const { t } = useTranslation('assignment');
  const showAutoSplitWarning = kind === 'whole_list' && location === 'pool' && pickList.auto_split_pending;
  const showShardSuffix = (kind === 'self_claim_shard' || kind === 'wizard_shard')
                       && shardIndex !== undefined && shardTotal !== undefined;
  const showClaimOrder = location === 'op' && claimOrder !== undefined && claimOrder !== null;

  const colliTotal = qtyAssignedTotal ?? pickList.qty_total;
  const taskTotal  = taskCount ?? pickList.item_count;
  const locTotal   = pickList.distinct_locations;
  const extraCustomers = Math.max(0, (pickList.customer_count ?? 0) - 1);

  // ICM (indice calore missione) per-card: emoji + valore, fasce vp-heat-*.
  const ix = calculatePickingIndexes({ locations: locTotal, totalQtyAssigned: colliTotal });

  const b = badgeFor(kind, location, status);
  const badgeLabel = t(b.labelKey);
  // Solo le shard (missioni splittate) ricevono la tinta per-distinta: una
  // whole_list è una card singola, senza sorelle da abbinare.
  const isShard = kind === 'self_claim_shard' || kind === 'wizard_shard';
  const splitTint = isShard ? splitTintOf(pickList.id) : undefined;
  const isDone = location === 'op' && status === 'done';
  const isActive = location === 'op' && status === 'active';
  const isInteractive = !(status === 'active' || status === 'done');

  // useId() per univocità tra le N card della board (UncontrolledTooltip risolve
  // il target via getElementById, IDs devono essere unici nel DOM).
  const uid = useId().replace(/:/g, '');
  const ttId = (k: string) => `tt-${uid}-${k}`;

  const cardClass = [
    'mb-2 shadow-none',
    isSelected ? 'border border-primary border-2' : isActive ? 'border vp-mission-active' : 'border',
    isDone ? 'opacity-75' : '',
  ].filter(Boolean).join(' ');

  return (
    <Card
      onClick={onClick}
      aria-label={pickList.external_id}
      aria-pressed={isSelected ?? false}
      className={cardClass}
      style={{ cursor: isInteractive ? 'grab' : 'default', backgroundColor: splitTint }}
    >
      <CardBody className="p-2">
        {/* Riga 1: status badge + shard suffix + warn/grip */}
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span id={ttId('state')} className={`badge ${b.bg} ${b.text} fs-11 fw-medium`}>
            <i className={`${b.icon} align-bottom`} /> {badgeLabel}
          </span>
          <UncontrolledTooltip target={ttId('state')} placement="top">
            {t('card.stateTooltip', { label: badgeLabel })}
          </UncontrolledTooltip>
          <div className="d-flex align-items-center gap-2">
            <span id={ttId('icm')} className={`badge rounded-pill vp-heat vp-heat-${ix.heatLevel}`}>
              <span aria-hidden="true">{HEAT_EMOJI[ix.heatLevel]}</span>
              <span className="fw-semibold ms-1">{ix.icm}</span>
            </span>
            <UncontrolledTooltip target={ttId('icm')} placement="top">
              {t('card.icmTooltip', { value: ix.icm })}
            </UncontrolledTooltip>
            {showShardSuffix && (
              <>
                <span id={ttId('shard')} className="text-muted small fw-medium">
                  #{shardIndex}/{shardTotal}
                </span>
                <UncontrolledTooltip target={ttId('shard')} placement="top">
                  {t('card.shardTooltip', { shardIndex, shardTotal })}
                </UncontrolledTooltip>
              </>
            )}
            {showAutoSplitWarning && (
              <>
                <i id={ttId('warn')} className="bi bi-exclamation-triangle text-warning fs-15"
                   aria-label={t('card.autoSplitPendingAria')} />
                <UncontrolledTooltip target={ttId('warn')} placement="top">
                  {t('card.autoSplitPendingTooltip')}
                </UncontrolledTooltip>
              </>
            )}
            {isInteractive && (
              <i className="bi bi-grip-vertical text-muted fs-15" aria-hidden="true" />
            )}
          </div>
        </div>

        {/* Riga 2: codice grande monospace, verde brand */}
        <h6 className="fs-15 fw-bold mb-2 font-monospace text-primary text-truncate">
          {pickList.external_id}
        </h6>

        {/* Riga 3: cliente principale (codice verde + ragione sociale, +N se multi) */}
        {pickList.customer_code && (
          <div className="d-flex align-items-center gap-1 fs-12 mb-2 text-truncate">
            <i className="bi bi-shop text-muted flex-shrink-0" />
            <span className="fw-bold text-primary flex-shrink-0">{pickList.customer_code}</span>
            {pickList.customer_name && (
              <>
                <span className="text-muted flex-shrink-0">·</span>
                <span className="text-truncate">{pickList.customer_name}</span>
              </>
            )}
            {extraCustomers > 0 && (
              <>
                <span id={ttId('cust')} className="badge bg-light text-muted flex-shrink-0">+{extraCustomers}</span>
                <UncontrolledTooltip target={ttId('cust')} placement="top">
                  {t('card.multiCustomer', { count: pickList.customer_count ?? 0 })}
                </UncontrolledTooltip>
              </>
            )}
          </div>
        )}

        {/* Riga 4: contatori compatti con icone + tooltip parlanti */}
        <div className="d-flex flex-wrap align-items-center gap-3 fs-12 text-muted">
          <span id={ttId('colli')}>
            <i className="bi bi-box-seam align-bottom" /> {colliTotal}
          </span>
          <UncontrolledTooltip target={ttId('colli')} placement="top">{t('card.totalPackages')}</UncontrolledTooltip>

          <span id={ttId('task')}>
            <i className="bi bi-list-check align-bottom" /> {taskTotal}
          </span>
          <UncontrolledTooltip target={ttId('task')} placement="top">{t('card.taskCount')}</UncontrolledTooltip>

          <span id={ttId('loc')}>
            <i className="bi bi-geo-alt align-bottom" /> {locTotal}
          </span>
          <UncontrolledTooltip target={ttId('loc')} placement="top">{t('card.distinctLocations')}</UncontrolledTooltip>

          {showClaimOrder && (
            <>
              <span id={ttId('claim')} className="ms-auto fw-semibold text-secondary">
                {t('card.queuedAtPosition', { position: claimOrder })}
              </span>
              <UncontrolledTooltip target={ttId('claim')} placement="top">
                {t('card.queuedAtPosition_tooltip', { defaultValue: 'Ordine di lavorazione nella coda di questo operatore' })}
              </UncontrolledTooltip>
            </>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
